import { Box } from "@mui/material"
import intl from "src/internationalization/intl"
import { defaultLocale } from "src/internationalization/locales"
import { Chip, Typography } from "src/lib"
import { useCommitmentsStore } from "src/store"
import { AllocationDetails, ReferencePortfolio, StrategyAllocation } from "src/services/api/commitments/types"

const normalizeProfileKey = (perfil?: string | null): string | null => {
    const upper = perfil?.toUpperCase()
    if (!upper) return null
    if (upper === "BALANCEADO") return "MODERADO"
    return upper
}

type ReferenceKey = Exclude<keyof ReferencePortfolio, "perfil">

interface StrategyMetadata {
    referenceKey?: ReferenceKey
    label: string
}

/** Metadados só das estratégias conhecidas (rótulo traduzido + chave equivalente na ReferencePortfolio). As linhas exibidas vêm de allocationDetails.allocations, não daqui. */
const KNOWN_STRATEGIES: Record<string, StrategyMetadata> = {
    Posfixado: { referenceKey: "posfixado", label: intl("allocationStrategyPosfixado") },
    Prefixado: { referenceKey: "prefixado", label: intl("allocationStrategyPrefixado") },
    Inflacao: { referenceKey: "inflacao", label: intl("allocationStrategyInflacao") },
    RetornoAbsolutoMm: { referenceKey: "retorno_absoluto_mm", label: intl("allocationStrategyRetornoAbsoluto") },
    RendaVariavel: { referenceKey: "renda_variavel", label: intl("allocationStrategyRendaVariavel") },
    Alternativo: { referenceKey: "alternativo", label: intl("allocationStrategyAlternativo") },
    RendaFixaGlobal: { referenceKey: "renda_fixa_global", label: intl("allocationStrategyRendaFixaGlobal") },
    RendaVariavelGlobal: { referenceKey: "renda_variavel_global", label: intl("allocationStrategyRendaVariavelGlobal") },
    Internacional: { label: intl("allocationStrategyInternacional") },
    Multimercado: { label: intl("allocationStrategyMultimercado") },
    NaoClassificada: { label: intl("allocationStrategyNaoClassificada") },
    NaoInformada: { label: intl("allocationStrategyNaoInformada") },
}

const PREFERRED_ORDER = Object.keys(KNOWN_STRATEGIES)

/** NovaEstrategia -> "Nova Estrategia", pra uma estratégia que allocationDetails manda mas ainda não conhecemos. */
const fallbackLabel = (strategy: string): string => strategy.replace(/([A-Z])/g, " $1").trim()

const labelForStrategy = (strategy: string): string => KNOWN_STRATEGIES[strategy]?.label ?? fallbackLabel(strategy)

/** "RETORNO ABSOLUTO MM" -> "RetornoAbsolutoMm" — mesma convenção que allocationDetails.allocations[].strategy usa. */
const toAllocationStrategyKey = (rawStrategy: string): string =>
    rawStrategy
        .trim()
        .split(/\s+/)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join("")

interface SimulatedInvestment {
    /** Estratégia do produto recomendado, no formato cru do backend (ex.: "RETORNO ABSOLUTO MM"). */
    strategy: string
    amount: number
    /** Estratégia do produto que está vencendo (cru). Quando presente, desconta o valor dali em vez de tratar como dinheiro novo. */
    fromStrategy?: string | null
}

/**
 * Move o valor simulado pra estratégia do produto selecionado. Se fromStrategy vier preenchido (vencimento com
 * estratégia conhecida), desconta de lá primeiro — é realocação, o total não muda. Sem fromStrategy (caixa
 * disponível, ou vencimento sem estratégia identificada), soma como dinheiro novo, entrando no total também.
 */
const applySimulation = (allocationDetails: AllocationDetails, simulation: SimulatedInvestment): AllocationDetails => {
    const toKey = toAllocationStrategyKey(simulation.strategy)
    const fromKey = simulation.fromStrategy ? toAllocationStrategyKey(simulation.fromStrategy) : null

    let allocations = fromKey
        ? allocationDetails.allocations.map((a) =>
              a.strategy === fromKey ? { ...a, value: Math.max(0, a.value - simulation.amount) } : a,
          )
        : allocationDetails.allocations

    const alreadyHasTo = allocations.some((a) => a.strategy === toKey)
    allocations = alreadyHasTo
        ? allocations.map((a) => (a.strategy === toKey ? { ...a, value: a.value + simulation.amount } : a))
        : [...allocations, { strategy: toKey, value: simulation.amount }]

    return {
        ...allocationDetails,
        allocations,
        allocationTotal: safeNumber(allocationDetails.allocationTotal) + (fromKey ? 0 : simulation.amount),
    }
}

/** Estratégias exibidas = exatamente as que allocationDetails.allocations manda, não uma lista fixa — se vier menos, mostra menos; se vier mais, mostra mais. */
const sortedAllocations = (allocationDetails: AllocationDetails): StrategyAllocation[] =>
    [...allocationDetails.allocations].sort((a, b) => {
        const indexA = PREFERRED_ORDER.indexOf(a.strategy)
        const indexB = PREFERRED_ORDER.indexOf(b.strategy)
        if (indexA === -1 && indexB === -1) return labelForStrategy(a.strategy).localeCompare(labelForStrategy(b.strategy))
        if (indexA === -1) return 1
        if (indexB === -1) return -1
        return indexA - indexB
    })

const BAR_COLOR = "secondary.main"
const TRACK_HEIGHT = 10

const safeNumber = (value: unknown): number => {
    const n = Number(value)
    return Number.isFinite(n) ? n : 0
}

/** allocations[].value vem em valor absoluto (R$); converte pra percentual (0-100) dividindo por allocationTotal. */
const strategyPercent = (value: number, allocationTotal: number): number => {
    const total = safeNumber(allocationTotal)
    if (total <= 0) return 0
    return (safeNumber(value) / total) * 100
}

/** ReferencePortfolio vem em fração 0-1; converte pra percentual (0-100). Sem valor na referência -> 0. */
const targetPercent = (fraction: number | null | undefined): number => safeNumber(fraction) * 100

const clampPct = (value: number): number => Math.max(0, Math.min(100, value))

const formatPct = (value: number): string =>
    `${value.toLocaleString(defaultLocale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`

interface StrategyRowProps {
    label: string
    color: string
    current: number
    target: number
}

const StrategyRow = ({ label, color, current, target }: StrategyRowProps) => {
    const fillPct = clampPct(current)

    return (
        <>
            <Typography
                variant="caption"
                color="quaternary"
                noWrap
                sx={{ alignSelf: "center", fontSize: "0.6875rem", pb: 0 }}
            >
                {label}
            </Typography>
            <Box
                sx={{
                    position: "relative",
                    alignSelf: "center",
                    height: TRACK_HEIGHT,
                    borderRadius: 0.75,
                    bgcolor: "neutral.darkest",
                    border: "1px solid rgba(255, 255, 255, 0.06)",
                    mb: 0,
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        height: "100%",
                        width: `${fillPct}%`,
                        bgcolor: color,
                        borderRadius: 0.75,
                        transition: "width 240ms ease",
                    }}
                />
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        bottom: 0,
                        left: `${clampPct(target)}%`,
                        width: "2px",
                        bgcolor: "neutral.lighter",
                        boxShadow: "0 0 0 1px rgba(10, 11, 12, 0.6)",
                    }}
                />
            </Box>
            <Box sx={{ alignSelf: "center", display: "flex", alignItems: "baseline" }}>
                <Typography
                    variant="caption"
                    noWrap
                    sx={(theme) => ({ textAlign: "right", fontWeight: (theme as any).typography.weights.bold, color, minWidth: "6ch" })}
                >
                    {formatPct(current)}
                </Typography>
                <Typography variant="caption" sx={{ color: "neutral.lighter", px: 0.5 }}>
                    |
                </Typography>
                <Typography
                    variant="caption"
                    noWrap
                    sx={(theme) => ({
                        textAlign: "right",
                        fontWeight: (theme as any).typography.weights.bold,
                        color: "neutral.lighter",
                        minWidth: "6ch",
                    })}
                >
                    {formatPct(target)}
                </Typography>
            </Box>
        </>
    )
}

interface AllocationStrategyBarsProps {
    allocationDetails?: AllocationDetails | null
    perfil?: string | null
    /** Quando presente, soma esse valor na estratégia do produto selecionado antes de exibir — só um "e se". */
    simulatedInvestment?: SimulatedInvestment
}

const AllocationStrategyBars = ({ allocationDetails, perfil, simulatedInvestment }: AllocationStrategyBarsProps) => {
    const referencePortfolios = useCommitmentsStore((s) => s.commitmentsData?.reference_portfolios)
    const profileKey = normalizeProfileKey(perfil)
    const reference = profileKey
        ? referencePortfolios?.find((rp) => rp.perfil?.toUpperCase() === profileKey)
        : undefined
    const hasNoReferenceData = !referencePortfolios || referencePortfolios.length === 0
    const displayedAllocationDetails =
        allocationDetails && simulatedInvestment ? applySimulation(allocationDetails, simulatedInvestment) : allocationDetails

    return (
        <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="quaternary" sx={{ display: "block", mb: 0.5 }}>
                {intl("allocationByStrategyTitle")}
                {simulatedInvestment && ` · ${intl("allocationByStrategySimulated")}`}
            </Typography>
            {!displayedAllocationDetails ? (
                <Typography variant="caption" color="quaternary">
                    {intl("allocationByStrategyNoAllocation")}
                </Typography>
            ) : hasNoReferenceData ? (
                <Typography variant="caption" color="quaternary">
                    {intl("allocationByStrategyDataUnavailable")}
                </Typography>
            ) : !reference ? (
                <Typography variant="caption" color="quaternary">
                    {intl("allocationByStrategyNoReferenceProfile")}
                </Typography>
            ) : (
                <Box sx={{ display: "grid", gridTemplateColumns: "auto minmax(60px, 1fr) auto", columnGap: 1 }}>
                    {sortedAllocations(displayedAllocationDetails).map((allocation) => {
                        const referenceKey = KNOWN_STRATEGIES[allocation.strategy]?.referenceKey
                        return (
                            <StrategyRow
                                key={allocation.strategy}
                                label={labelForStrategy(allocation.strategy)}
                                color={BAR_COLOR}
                                current={strategyPercent(allocation.value, displayedAllocationDetails.allocationTotal ?? 0)}
                                target={targetPercent(referenceKey ? reference[referenceKey] : undefined)}
                            />
                        )
                    })}
                </Box>
            )}
        </Box>
    )
}

export { AllocationStrategyBars }
